import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatDistanceToNowStrict } from 'date-fns';
import { FolderListItem } from '../../services/api/folderService';
import { getDateFnsLocale } from '../../utils/dateUtils';
import { PinButton } from './PinButton';
import { folderItemCount } from './folderCounts';
import { CategoryChip } from '../ui-components/category/CategoryChip';

interface FolderListTableProps {
    folders: FolderListItem[];
    onTogglePin?: (folderId: string, nextIsPinned: boolean) => void;
    pendingPinId?: string | null;
}

const GRID_COLS =
    'grid gap-x-3 gap-y-2 px-3 md:grid-cols-[minmax(0,1fr)_150px_190px_100px_32px] md:items-center md:gap-y-0';

export const FolderListTable = ({
    folders,
    onTogglePin,
    pendingPinId,
}: FolderListTableProps) => {
    const { t, i18n } = useTranslation();
    const locale = getDateFnsLocale(i18n.language);

    return (
        <div className="flex flex-col">
            <div
                className={`${GRID_COLS} hidden border-b border-subtle py-2 text-[10px] font-medium uppercase tracking-wide text-subtle-foreground md:grid`}
            >
                <span>{t('folders.overview.table.name')}</span>
                <span>{t('folders.overview.table.category')}</span>
                <span>{t('folders.overview.table.items')}</span>
                <span>{t('folders.overview.table.updated')}</span>
                <span />
            </div>
            {folders.map((folder) => (
                <Link
                    key={folder._id}
                    to={`/group/${folder._id}`}
                    className={`${GRID_COLS} border-b border-subtle py-3 transition-colors hover:bg-muted`}
                >
                    <span
                        className="col-span-1 row-start-1 min-w-0 break-words text-xs font-medium text-foreground md:line-clamp-2"
                        title={folder.title}
                    >
                        {folder.title}
                    </span>
                    <span className="col-span-1 row-start-2 min-w-0 md:row-auto">
                        {folder.category ? (
                            <CategoryChip category={folder.category} />
                        ) : (
                            <span className="text-xs text-subtle-foreground">
                                —
                            </span>
                        )}
                    </span>
                    <span className="hidden min-w-0 text-xs font-medium text-subtle-foreground md:col-span-1 md:row-auto md:block">
                        {[
                            folder.subFolderCount > 0 &&
                                t('folders.overview.counts.subFolders', {
                                    count: folder.subFolderCount,
                                }),
                            folderItemCount(folder) > 0 &&
                                t('folders.overview.counts.items', {
                                    count: folderItemCount(folder),
                                }),
                        ]
                            .filter(Boolean)
                            .join(' · ') || t('folders.overview.counts.empty')}
                    </span>
                    <span className="col-start-2 row-start-2 justify-self-end text-xs text-subtle-foreground md:col-auto md:row-auto md:justify-self-auto">
                        {formatDistanceToNowStrict(new Date(folder.createdAt), {
                            addSuffix: true,
                            locale,
                        })}
                    </span>
                    {onTogglePin ? (
                        <span className="col-start-2 row-start-1 justify-self-end md:col-auto md:row-auto md:justify-self-auto">
                            <PinButton
                                isPinned={folder.isPinned}
                                onToggle={() =>
                                    onTogglePin(folder._id, !folder.isPinned)
                                }
                                disabled={pendingPinId === folder._id}
                            />
                        </span>
                    ) : (
                        <span className="hidden md:block" />
                    )}
                </Link>
            ))}
        </div>
    );
};
